import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, Save, ChevronUp, ChevronDown, GripVertical, CalendarArrowUp } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, todayISO, addDaysISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const GST_RATE = 0.05;
const NONE = "__none__";
let lineKey = 0;
const withKey = (l) => ({ date: "", ...l, _k: ++lineKey });
const emptyLine = () => withKey({ description: "", quantity: 1, unit_price: 0 });

export default function InvoiceEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const { currentId } = useCompany();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [billed, setBilled] = useState({ time_ids: [], expense_ids: [] });
  const [pos, setPos] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [form, setForm] = useState({
    client_id: "",
    invoice_number: "",
    issue_date: todayISO(),
    due_date: addDaysISO(30),
    status: "draft",
    notes: "",
    job_id: NONE,
    po_id: "",
    po_number: "",
    client_job_number: "",
    line_items: [emptyLine()],
  });

  useEffect(() => {
    if (!currentId) return;
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/jobs").then((r) => setJobs(r.data));
    if (!isNew) {
      api.get(`/invoices/${id}`).then((r) => {
        const inv = r.data;
        setForm({
          client_id: inv.client_id,
          invoice_number: inv.invoice_number || "",
          issue_date: inv.issue_date,
          due_date: inv.due_date,
          status: inv.status,
          notes: inv.notes || "",
          job_id: inv.job_id || NONE,
          po_id: inv.po_id || "",
          po_number: inv.po_number || "",
          client_job_number: inv.client_job_number || "",
          line_items: inv.line_items.length ? inv.line_items.map((l) => withKey({
            date: l.date || "",
            description: l.description,
            quantity: l.quantity,
            unit_price: l.unit_price,
          })) : [emptyLine()],
        });
        if (inv.job_id) api.get(`/purchase-orders?job_id=${inv.job_id}`).then((r) => setPos(r.data));
      });
    }
  }, [currentId, id, isNew]);

  const setLine = (i, key, val) => {
    const items = [...form.line_items];
    items[i] = { ...items[i], [key]: val };
    setForm({ ...form, line_items: items });
  };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, emptyLine()] });
  const removeLine = (i) =>
    setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) });
  const moveLine = (from, to) => {
    if (to < 0 || to >= form.line_items.length || from === to) return;
    const items = [...form.line_items];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    setForm({ ...form, line_items: items });
  };
  const sortByDate = () => {
    const items = [...form.line_items].sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date.localeCompare(b.date);
    });
    setForm({ ...form, line_items: items });
  };

  const onJobChange = async (v) => {
    if (v === NONE) {
      setForm((f) => ({ ...f, job_id: v }));
      setBilled({ time_ids: [], expense_ids: [] });
      setPos([]);
      return;
    }
    setForm((f) => ({ ...f, job_id: v }));
    try {
      const r = await api.get(`/jobs/${v}/invoice-preview`);
      const { client_id, line_items, time_ids, expense_ids, client_job_number, purchase_orders } = r.data;
      setPos(purchase_orders || []);
      setForm((f) => ({
        ...f,
        job_id: v,
        client_id: client_id || f.client_id,
        client_job_number: client_job_number || f.client_job_number,
      }));
      if (!line_items.length) {
        setBilled({ time_ids: [], expense_ids: [] });
        toast.info("No unbilled time or expenses on this job to pull in");
        return;
      }
      const jobLines = line_items.map((l) => withKey({
        date: l.date || "",
        description: l.description,
        quantity: l.quantity,
        unit_price: l.unit_price,
      }));
      setForm((f) => {
        const existing = f.line_items.filter((l) => l.description.trim());
        return { ...f, line_items: [...existing, ...jobLines] };
      });
      setBilled({ time_ids: time_ids || [], expense_ids: expense_ids || [] });
      toast.success(`Pulled in ${jobLines.length} line${jobLines.length > 1 ? "s" : ""} from the job`);
    } catch (e) {
      toast.error("Could not load job lines");
    }
  };

  const onPoChange = (v) => {
    if (v === NONE) return setForm((f) => ({ ...f, po_id: "", po_number: "" }));
    const po = pos.find((p) => p.id === v);
    setForm((f) => ({ ...f, po_id: v, po_number: po ? po.po_number : f.po_number }));
  };

  const subtotal = form.line_items.reduce(
    (s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0),
    0
  );
  const gst = subtotal * GST_RATE;
  const total = subtotal + gst;

  const save = async () => {
    if (!form.client_id) return toast.error("Select a client");
    const valid = form.line_items.filter((l) => l.description.trim());
    if (!valid.length) return toast.error("Add at least one line item");
    const payload = {
      ...form,
      job_id: form.job_id === NONE ? null : form.job_id,
      billed_time_ids: isNew ? billed.time_ids : [],
      billed_expense_ids: isNew ? billed.expense_ids : [],
      line_items: valid.map((l) => ({
        date: l.date || "",
        description: l.description,
        quantity: parseFloat(l.quantity) || 0,
        unit_price: parseFloat(l.unit_price) || 0,
      })),
    };
    try {
      if (isNew) await api.post("/invoices", payload);
      else await api.put(`/invoices/${id}`, payload);
      toast.success(isNew ? "Invoice created" : "Invoice updated");
      navigate("/invoices");
    } catch (e) {
      toast.error("Could not save invoice");
    }
  };

  return (
    <div className="max-w-4xl">
      <button
        onClick={() => navigate("/invoices")}
        data-testid="back-to-invoices"
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back to invoices
      </button>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 mb-6">
        {isNew ? "New Invoice" : "Edit Invoice"}
      </h1>

      <Card className="p-6 rounded-lg border-slate-200 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <Label>Client</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="invoice-client-select" className="mt-1 rounded-md">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="invoice-status-select" className="mt-1 rounded-md capitalize">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Issue Date</Label>
            <div className="mt-1">
              <DatePicker testId="invoice-issue-date" value={form.issue_date} onChange={(d) => setForm({ ...form, issue_date: d })} />
            </div>
          </div>
          <div>
            <Label>Due Date</Label>
            <div className="mt-1">
              <DatePicker testId="invoice-due-date" value={form.due_date} onChange={(d) => setForm({ ...form, due_date: d })} />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Job (optional — pulls in unbilled lines)</Label>
            <Select value={form.job_id} onValueChange={onJobChange}>
              <SelectTrigger data-testid="invoice-job-select" className="mt-1 rounded-md">
                <SelectValue placeholder="No job" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value={NONE}>No job</SelectItem>
                {jobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>{j.code ? `${j.code} · ` : ""}{j.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>PO # (optional)</Label>
            <Select value={form.po_id || NONE} onValueChange={onPoChange}>
              <SelectTrigger data-testid="invoice-po-select" className="mt-1 rounded-md">
                <SelectValue placeholder="No PO" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value={NONE}>No PO</SelectItem>
                {pos.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.po_number}{p.amount ? ` · ${"$"}${p.amount}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Client Job #</Label>
            <Input data-testid="invoice-client-job-number-input" value={form.client_job_number} onChange={(e) => setForm({ ...form, client_job_number: e.target.value })} className="mt-1 rounded-md" placeholder="Client's reference" />
          </div>
          <div className="md:col-span-2">
            <Label>Invoice # (optional — auto if blank)</Label>
            <Input data-testid="invoice-number-input" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="mt-1 rounded-md" placeholder="e.g. 25-1440" />
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <Label>Line Items</Label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" data-testid="sort-lines-by-date-btn" onClick={sortByDate} className="rounded-md">
                <CalendarArrowUp className="h-4 w-4 mr-1" /> Sort by date
              </Button>
              <Button variant="outline" size="sm" data-testid="add-line-item-btn" onClick={addLine} className="rounded-md">
                <Plus className="h-4 w-4 mr-1" /> Add line
              </Button>
            </div>
          </div>
          <div className="border border-slate-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-200">
                  <th className="w-14"></th>
                  <th className="px-3 py-2 font-medium w-40">Date</th>
                  <th className="px-3 py-2 font-medium">Description</th>
                  <th className="px-3 py-2 font-medium w-20 text-right">Qty</th>
                  <th className="px-3 py-2 font-medium w-32 text-right">Unit Price</th>
                  <th className="px-3 py-2 font-medium w-28 text-right">Amount</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.line_items.map((l, i) => (
                  <tr
                    key={l._k}
                    data-testid={`line-row-${i}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { moveLine(dragIdx, i); setDragIdx(null); }}
                    className={`border-b border-slate-50 transition-colors ${dragIdx === i ? "bg-slate-100 opacity-60" : ""}`}
                  >
                    <td className="pl-2 py-2">
                      <div className="flex items-center gap-0.5">
                        <span
                          draggable
                          data-testid={`line-drag-${i}`}
                          onDragStart={() => setDragIdx(i)}
                          onDragEnd={() => setDragIdx(null)}
                          className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700"
                          title="Drag to reorder"
                        >
                          <GripVertical className="h-4 w-4" />
                        </span>
                        <div className="flex flex-col">
                          <button data-testid={`line-up-${i}`} disabled={i === 0} onClick={() => moveLine(i, i - 1)} className="text-slate-400 hover:text-slate-800 disabled:opacity-25" title="Move up">
                            <ChevronUp className="h-3.5 w-3.5" />
                          </button>
                          <button data-testid={`line-down-${i}`} disabled={i === form.line_items.length - 1} onClick={() => moveLine(i, i + 1)} className="text-slate-400 hover:text-slate-800 disabled:opacity-25" title="Move down">
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        type="date"
                        data-testid={`line-date-${i}`}
                        value={l.date || ""}
                        onChange={(e) => setLine(i, "date", e.target.value)}
                        className="rounded-md border-slate-200 tabular-nums"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        data-testid={`line-desc-${i}`}
                        value={l.description}
                        onChange={(e) => setLine(i, "description", e.target.value)}
                        placeholder="Service or item"
                        className="rounded-md border-slate-200"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        step="0.01"
                        data-testid={`line-qty-${i}`}
                        value={l.quantity}
                        onChange={(e) => setLine(i, "quantity", e.target.value)}
                        className="rounded-md border-slate-200 text-right tabular-nums"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        step="0.01"
                        data-testid={`line-price-${i}`}
                        value={l.unit_price}
                        onChange={(e) => setLine(i, "unit_price", e.target.value)}
                        className="rounded-md border-slate-200 text-right tabular-nums"
                      />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      {money((parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0))}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {form.line_items.length > 1 && (
                        <button data-testid={`remove-line-${i}`} onClick={() => removeLine(i)}>
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <Label>Notes</Label>
            <Textarea
              data-testid="invoice-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 rounded-md"
              placeholder="Payment terms, thank you note…"
            />
          </div>
          <div className="w-full md:w-64 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Subtotal</span>
              <span className="tabular-nums" data-testid="invoice-subtotal">{money(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">GST (5%)</span>
              <span className="tabular-nums" data-testid="invoice-gst">{money(gst)}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-2 font-heading font-bold text-lg">
              <span>Total</span>
              <span className="tabular-nums" data-testid="invoice-total">{money(total)}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button data-testid="save-invoice-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800">
            <Save className="h-4 w-4 mr-2" /> {isNew ? "Create Invoice" : "Save Changes"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
